import type * as THREE from 'three';

/**
 * GPU time of the rendered frame (EXT_disjoint_timer_query_webgl2), read back a few frames late
 * without stalling. Drives the adaptive resolution: frame time alone can't tell a GPU-bound frame
 * (resolution helps) from a CPU-bound one (it doesn't), and under vsync it never shows headroom.
 */
export class GpuClock {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null;
  private readonly pending: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  /** Smoothed GPU milliseconds per frame; NaN until the first result (or without the extension). */
  ms = NaN;
  /** Raw samples since the last `drain()` (for the ?stats overlay). */
  private readonly recent: number[] = [];

  constructor(renderer: THREE.WebGLRenderer) {
    this.gl = renderer.getContext() as WebGL2RenderingContext;
    this.ext = this.gl.getExtension('EXT_disjoint_timer_query_webgl2');
  }

  get available() { return !!this.ext; }

  begin() {
    if (!this.ext || this.active || this.pending.length > 4) return;
    this.active = this.gl.createQuery()!;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, this.active);
  }

  end() {
    const gl = this.gl, ext = this.ext;
    if (!ext) return;
    if (this.active) { gl.endQuery(ext.TIME_ELAPSED_EXT); this.pending.push(this.active); this.active = null; }
    while (this.pending.length && gl.getQueryParameter(this.pending[0], gl.QUERY_RESULT_AVAILABLE)) {
      const q = this.pending.shift()!;
      if (!gl.getParameter(ext.GPU_DISJOINT_EXT)) {
        const v = gl.getQueryParameter(q, gl.QUERY_RESULT) / 1e6;
        this.ms = Number.isNaN(this.ms) ? v : this.ms + (v - this.ms) * 0.08;
        if (this.recent.length < 512) this.recent.push(v);
      }
      gl.deleteQuery(q);
    }
  }

  /** Samples gathered since the previous call. */
  drain() { const out = this.recent.slice(); this.recent.length = 0; return out; }
}
